import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
} from '@fluentui/react-components';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { useTranslation } from 'react-i18next';

import { useStorageDialogsStyles } from './StorageDialogs.Styles';
import { ErrorType } from '../../../models/HttpError';

export interface ConfirmActionDialogProps {
  open: boolean;
  title: string;
  /** Main question, e.g. "¿Eliminar factura.pdf?" */
  message: React.ReactNode;
  /** Optional consequence shown as a warning banner */
  warning?: string;
  confirmLabel: string;
  /** Rejects with ErrorType: the dialog stays open showing the reason */
  onConfirm: () => Promise<void>;
  onDismiss: () => void;
  describeError: (error: ErrorType) => string;
  errorTitle: string;
  testId?: string;
}

/** Confirmation for destructive actions (delete file / folder, unlink). */
export const ConfirmActionDialog = (props: ConfirmActionDialogProps) => {
  const { t } = useTranslation();
  const styles = useStorageDialogsStyles();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<ErrorType | undefined>(undefined);
  const mounted = React.useRef(true);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (props.open) {
      setBusy(false);
      setError(undefined);
    }
  }, [props.open]);

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      await props.onConfirm();
      if (mounted.current) setBusy(false);
    } catch (failure: unknown) {
      if (!mounted.current) return;
      setBusy(false);
      setError(failure as ErrorType);
    }
  };

  return (
    <Dialog
      open={props.open}
      modalType="alert"
      onOpenChange={(_, data) => {
        if (!data.open && !busy) props.onDismiss();
      }}
    >
      <DialogSurface data-testid={props.testId ?? 'confirm-dialog'}>
        <DialogBody>
          <DialogTitle>{props.title}</DialogTitle>
          <DialogContent className={styles.content}>
            <p className={styles.text}>{props.message}</p>
            {props.warning && (
              <GuideMessageBar intent="warning" layout="multiline">
                <MessageBarBody>{props.warning}</MessageBarBody>
              </GuideMessageBar>
            )}
            {error && (
              <GuideMessageBar intent="error" layout="multiline" data-testid="confirm-dialog-error">
                <MessageBarBody>
                  <MessageBarTitle>{props.errorTitle}</MessageBarTitle>
                  {props.describeError(error)}
                </MessageBarBody>
              </GuideMessageBar>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={props.onDismiss} disabled={busy}>
              {t('filesExplorer.actions.cancel')}
            </Button>
            <Button
              appearance="primary"
              onClick={confirm}
              disabled={busy}
              icon={busy ? <Spinner size="extra-tiny" /> : undefined}
              data-testid="confirm-dialog-confirm"
            >
              {props.confirmLabel}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ConfirmActionDialog;
